import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type AppliedFilters, type ChatSession, chatApi } from "./chatApi";
import { messagesKey, sessionsKey } from "./chatKeys";
import { startStream, stopStream } from "./chatStreamStore";

export function useChatSessions(projectId: string | undefined) {
  return useQuery({
    queryKey: sessionsKey(projectId ?? ""),
    queryFn: () => chatApi.listSessions(projectId!),
    enabled: !!projectId,
  });
}

export function useCreateChatSession(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ChatSession, Error, { title?: string }>({
    mutationFn: ({ title }) => chatApi.createSession(projectId, title),
    // Prepend the new session so the cached list already holds it when the
    // caller selects it (the auto-select effect would otherwise reset focus).
    onSuccess: (newSession) => {
      qc.setQueryData<ChatSession[]>(sessionsKey(projectId), (prev) => [
        newSession,
        ...(prev ?? []).filter((s) => s.id !== newSession.id),
      ]);
    },
  });
}

export function useRenameChatSession(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ChatSession, Error, { sessionId: string; title: string }>({
    mutationFn: ({ sessionId, title }) => chatApi.renameSession(sessionId, title),
    onSuccess: (session) => {
      qc.setQueryData<ChatSession[]>(sessionsKey(projectId), (prev) =>
        (prev ?? []).map((s) => (s.id === session.id ? session : s)),
      );
    },
  });
}

export function useUpdateChatSessionFilters(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ChatSession, Error, { sessionId: string; metadataFilters: Record<string, string[]> | null }>({
    mutationFn: ({ sessionId, metadataFilters }) => chatApi.updateSessionFilters(sessionId, metadataFilters),
    onSuccess: (session) => {
      qc.setQueryData<ChatSession[]>(sessionsKey(projectId), (prev) =>
        (prev ?? []).map((s) => (s.id === session.id ? session : s)),
      );
    },
  });
}

export function useDeleteChatSession(projectId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { sessionId: string }>({
    mutationFn: ({ sessionId }) => chatApi.deleteSession(sessionId),
    onSuccess: (_, { sessionId }) => {
      qc.setQueryData<ChatSession[]>(sessionsKey(projectId), (prev) => (prev ?? []).filter((s) => s.id !== sessionId));
      qc.invalidateQueries({ queryKey: sessionsKey(projectId) });
      qc.removeQueries({ queryKey: messagesKey(sessionId) });
    },
  });
}

export function useSessionMessages(sessionId: string | undefined) {
  return useQuery({
    queryKey: messagesKey(sessionId ?? ""),
    queryFn: async () => {
      // First page only; streamed turns are appended to the cache as they land.
      const page = await chatApi.listMessages(sessionId!, { limit: 200 });
      return page.items;
    },
    enabled: !!sessionId,
  });
}

/**
 * Send a message and stream the reply. The stream lives in chatStreamStore,
 * outside the React tree, so it survives this component unmounting; read the
 * live text with useChatStream(sessionId).
 */
export function useStreamChatMessage(sessionId: string, projectId: string) {
  const qc = useQueryClient();
  const stream = useCallback(
    (content: string, documentIds?: string[], appliedFilters?: AppliedFilters | null) =>
      startStream({ sessionId, projectId, queryClient: qc, content, documentIds, appliedFilters }),
    [sessionId, projectId, qc],
  );
  const cancel = useCallback(() => stopStream(sessionId), [sessionId]);
  return { stream, cancel };
}
